import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useAccessControl } from "@/hooks/useAccessControl";
import { getWidgetById } from "@/utils/widgets";
import { selectTemplate } from "../templates";
import { buildLayoutExportData } from "../templates/packLayout";
import { applyLayoutData } from "../templates/applyTemplate";
import type { OnboardingAnswers } from "../types";

interface StepDashboardProps {
  answers: OnboardingAnswers;
  selectedWatchlistIds: number[];
  /** Called with the applied template id (or "blank") once done. */
  onComplete: (appliedTemplateId: string) => void;
}

function baseIdOf(id: string): string {
  if (getWidgetById(id)) return id;
  const dashIdx = id.indexOf("-");
  return dashIdx === -1 ? id : id.substring(0, dashIdx);
}

/**
 * Welcome flow step 3: show the template matched to the user's profile and
 * build it. Everything shown is fully usable on their plan.
 */
export default function StepDashboard({
  answers,
  selectedWatchlistIds,
  onComplete,
}: StepDashboardProps) {
  const { hasAccessToWidget } = useAccessControl();
  const [building, setBuilding] = useState(false);

  const template = useMemo(() => selectTemplate(answers), [answers]);

  const widgetSummaries = useMemo(() => {
    const seen = new Set<string>();
    return template.widgets
      .map((spec) => getWidgetById(baseIdOf(spec.id)))
      .filter((w): w is NonNullable<typeof w> => !!w)
      .filter((w) => {
        const label = w.displayName ?? w.name;
        if (seen.has(label)) return false;
        seen.add(label);
        return true;
      });
  }, [template]);

  const handleBuild = () => {
    setBuilding(true);
    try {
      const data = buildLayoutExportData(template.name, template.widgets);
      applyLayoutData(data, {
        hasAccessToWidget,
        highlightWatchlistIds: selectedWatchlistIds,
      });
      toast.success(`Your "${template.name}" dashboard is ready`);
      onComplete(template.id);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to build dashboard"
      );
      setBuilding(false);
    }
  };

  return (
    <div>
      <h3 className="text-base font-semibold text-gray-100">
        Your starting dashboard
      </h3>
      <p className="mt-1 text-sm text-gray-400">
        Based on your answers we&apos;ll set up the{" "}
        <span className="font-medium text-gray-200">{template.name}</span>{" "}
        layout. Rearrange, resize or swap anything later.
      </p>

      <div className="mt-4 rounded-lg border border-gray-700 bg-gray-900/50 p-4">
        <div className="text-sm font-semibold text-gray-100">
          {template.name}
        </div>
        <p className="mt-1 text-xs text-gray-400 leading-relaxed">
          {template.description}
        </p>
        <ul className="mt-3 space-y-1.5">
          {widgetSummaries.map((widget) => (
            <li key={widget.id} className="text-xs text-gray-300">
              <span className="font-medium text-gray-200">
                {widget.displayName ?? widget.name}
              </span>
              {widget.description && (
                <span className="text-gray-500"> — {widget.description}</span>
              )}
            </li>
          ))}
        </ul>
        {selectedWatchlistIds.length > 0 && (
          <p className="mt-3 text-xs text-squawk-blue">
            Your {selectedWatchlistIds.length} selected watchlist
            {selectedWatchlistIds.length > 1 ? "s" : ""} will be highlighted in
            the headline feed.
          </p>
        )}
      </div>

      <div className="mt-5 flex flex-col sm:flex-row items-center gap-3">
        <button
          type="button"
          onClick={handleBuild}
          disabled={building}
          className="w-full sm:w-auto rounded-md bg-squawk-blue px-5 py-2.5 text-sm font-semibold text-white hover:bg-squawk-blue/90 transition-colors disabled:opacity-60 cursor-pointer"
        >
          {building ? "Building…" : "Build my dashboard"}
        </button>
        <button
          type="button"
          onClick={() => onComplete("blank")}
          disabled={building}
          className="text-sm text-gray-400 hover:text-gray-200 transition-colors cursor-pointer bg-transparent border-none"
        >
          Start with the default instead
        </button>
      </div>
    </div>
  );
}
